import { getMonthOrder } from '@exam-center/exam-event-management';
import {
  useGetAllExamEventsByExamCenterCodeQuery,
  ExamEventStatus as ExamEventStatuses,
  OrderType,
  FilterOperator,
  RegistrationStatus,
  RegistrationType,
  type GetAllExamEventsByExamCenterCodeInput,
} from '@exam-center/graphql';
import { useNavigate, useParams } from '@exam-center/router';
import { ErrorState, Flex, Loading, Text } from '@shared/components';
import { dayjs, groupBy } from '@shared/utils';
import { useMemo } from 'react';

import { ExamEventDailySchedule, ExamEventScheduleEmptyState } from '../../components';
import { useExamEventScheduleSearchParams, useGetDefaultDateRange } from '../../hooks';
import { ExamEventMenus } from '../exam-event-menus';

import {
  mapExamEventsToExamEventDailyScheduleProps,
  sortExamEvents,
  EXAM_EVENT_STATUS_TO_EXAM_EVENT_EMPTY_STATE_VARIANT,
} from './utils';

type ExamEventStatus = `${ExamEventStatuses}`;

const STATUS_ORDER: ExamEventStatus[] = [
  'SCHEDULED',
  'READY_TO_EXAM',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELED',
];

const STATUS_LABEL: Record<ExamEventStatus, string> = {
  SCHEDULED: 'กำหนดการสอบ',
  READY_TO_EXAM: 'เตรียมพร้อมสอบ',
  IN_PROGRESS: 'กำลังสอบ',
  COMPLETED: 'สอบเสร็จสิ้น',
  CANCELED: 'ยกเลิกรอบสอบ',
};

const DEFAULT_STATUSES = Object.values(ExamEventStatuses);

const DEFAULT_REGISTRATION_STATUSES = Object.values(RegistrationStatus);

const DEFAULT_REGISTRATION_TYPES = Object.values(RegistrationType);

export default function ExamEventSchedule() {
  const { examCenterCode, month, year } = useParams(
    '/:examCenterCode/exam-events/years/:year/months/:month',
  );

  const defaultDateRange = useGetDefaultDateRange({
    month,
    year,
  });
  const [{ status, registrationStatus, registrationType, startedAt, endedAt }] =
    useExamEventScheduleSearchParams({
      defaultValues: {
        startedAt: defaultDateRange[0],
        endedAt: defaultDateRange[1],
      },
    });
  const navigate = useNavigate();

  const yearNo = parseInt(year);

  const selectedMonth = useMemo(
    () => dayjs().set('year', yearNo).set('month', getMonthOrder(month)),
    [month, yearNo],
  );

  const startDate = startedAt
    ? dayjs(startedAt).startOf('day').set('year', yearNo).toDate()
    : selectedMonth.startOf('month').toDate();

  const endDate = endedAt
    ? dayjs(endedAt).set('year', yearNo).endOf('day').toDate()
    : selectedMonth.endOf('month').toDate();

  const getAllExamEventsByExamCenterCodeInput: GetAllExamEventsByExamCenterCodeInput = {
    examCenterCode,
    filter: {
      conditions: [
        {
          field: 'examStartedAt',
          operator: FilterOperator.Between,
          value: [startDate, endDate],
        },
        {
          field: 'status',
          operator: FilterOperator.In,
          value: status === 'ALL' ? DEFAULT_STATUSES : ([status] as ExamEventStatus[]),
        },
        {
          field: 'registrationStatus',
          operator: FilterOperator.In,
          value:
            registrationStatus.length === 0 ? DEFAULT_REGISTRATION_STATUSES : registrationStatus,
        },
        {
          field: 'registrationType',
          operator: FilterOperator.In,
          value: registrationType.length === 0 ? DEFAULT_REGISTRATION_TYPES : registrationType,
        },
      ],
    },
    order: {
      field: 'examStartedAt',
      type: OrderType.Asc,
    },
  };

  const {
    data,
    loading: isLoading,
    error,
  } = useGetAllExamEventsByExamCenterCodeQuery({
    variables: {
      getAllExamEventsByExamCenterCodeInput,
    },
    fetchPolicy: 'network-only',
  });

  const examEvents = useMemo(
    () => sortExamEvents(data?.ecGetAllExamEventsByExamCenterCode.examEvents ?? []),
    [data],
  );

  const examEventsGroupByStatus = useMemo(() => groupBy(examEvents, 'status'), [examEvents]);

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return <ErrorState />;
  }

  const displayStatus = status === 'ALL' ? STATUS_ORDER : ([status] as ExamEventStatus[]);

  return (
    <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(6) })}>
      {displayStatus.map((status) => {
        const events = examEventsGroupByStatus[status] ?? [];

        const groupByDate = groupBy(events, (event) =>
          dayjs(event.examStartedAt).format('YYYY-MM-DD'),
        );

        const orderedDate =
          Object.keys(groupByDate).sort((a, b) => (dayjs(b).isAfter(dayjs(a)) ? -1 : 1)) ?? [];
        return (
          <Flex key={status} sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(4) })}>
            <Text variant='h3'>
              {STATUS_LABEL[status]} ({events.length})
            </Text>
            <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(2) })}>
              {events.length > 0 ? (
                orderedDate.map((date) => {
                  const examEvents = mapExamEventsToExamEventDailyScheduleProps(
                    groupByDate[date],
                    navigate,
                  );

                  return (
                    <ExamEventDailySchedule
                      key={date}
                      date={dayjs(date).toDate()}
                      examEvents={examEvents.map((examEvent) => ({
                        ...examEvent,
                        endAdornment: <ExamEventMenus examEvent={examEvent} />,
                      }))}
                    />
                  );
                })
              ) : (
                <ExamEventScheduleEmptyState
                  variant={EXAM_EVENT_STATUS_TO_EXAM_EVENT_EMPTY_STATE_VARIANT[status]}
                />
              )}
            </Flex>
          </Flex>
        );
      })}
    </Flex>
  );
}
